import React from 'react';
import LegalPage from './LegalPage';

export default function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy" updated="April 15, 2026">

      <p>ClickSaver ("we", "us", or "our") is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.</p>

      <div className="legal-toc">
        <strong style={{ fontSize: 14, color: '#0f172a' }}>Table of Contents</strong>
        <ul style={{ marginTop: 10 }}>
          <li><a href="#collect">1. Information We Collect</a></li>
          <li><a href="#use">2. How We Use Your Information</a></li>
          <li><a href="#share">3. Information Sharing</a></li>
          <li><a href="#cookies">4. Cookies</a></li>
          <li><a href="#security">5. Data Security</a></li>
          <li><a href="#retention">6. Data Retention</a></li>
          <li><a href="#rights">7. Your Rights</a></li>
          <li><a href="#children">8. Children's Privacy</a></li>
          <li><a href="#changes">9. Changes to This Policy</a></li>
          <li><a href="#contact">10. Contact Us</a></li>
        </ul>
      </div>

      <h2 id="collect">1. Information We Collect</h2>
      <h3>Information You Provide</h3>
      <ul>
        <li><strong>Account Information:</strong> Name, email address, and password when you register.</li>
        <li><strong>Billing Information:</strong> Payment details are processed securely by Stripe. We do not store your full card details.</li>
        <li><strong>Campaign Data:</strong> Domain names, campaign settings, and Google Ads account IDs you configure.</li>
        <li><strong>Communications:</strong> Messages you send to our support team.</li>
      </ul>

      <h3>Information Collected Automatically</h3>
      <ul>
        <li><strong>Click Data:</strong> IP addresses, user agents, timestamps, and referral URLs of visitors to your tracked websites.</li>
        <li><strong>Usage Data:</strong> Pages visited, features used, and time spent in our dashboard.</li>
        <li><strong>Device Information:</strong> Browser type, operating system, and screen resolution.</li>
        <li><strong>Log Data:</strong> Server logs including IP addresses and access timestamps.</li>
      </ul>

      <div className="legal-divider" />

      <h2 id="use">2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide, operate, and maintain our click fraud detection services</li>
        <li>Process payments and manage your subscription</li>
        <li>Detect, analyze, and block fraudulent clicks on your campaigns</li>
        <li>Push fraudulent IP addresses to your Google Ads exclusion lists</li>
        <li>Send you transactional emails (receipts, alerts, reports)</li>
        <li>Respond to your support requests</li>
        <li>Improve and develop our services</li>
        <li>Comply with legal obligations</li>
      </ul>

      <div className="legal-divider" />

      <h2 id="share">3. Information Sharing</h2>
      <p>We do not sell, trade, or rent your personal information to third parties. We may share information with:</p>
      <ul>
        <li><strong>Service Providers:</strong> Stripe (payments), Firebase (authentication & database), Resend (email delivery), Render (hosting). These providers are bound by strict confidentiality obligations.</li>
        <li><strong>Google Ads API:</strong> IP addresses identified as fraudulent are shared with Google Ads via their API to apply IP exclusions to your campaigns.</li>
        <li><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or to protect the rights and safety of ClickSaver and its users.</li>
      </ul>

      <div className="legal-divider" />

      <h2 id="cookies">4. Cookies</h2>
      <p>We use cookies and similar tracking technologies to operate our service. Please see our <a href="/cookies">Cookie Policy</a> for detailed information.</p>

      <div className="legal-divider" />

      <h2 id="security">5. Data Security</h2>
      <p>We implement appropriate technical and organizational measures to protect your information:</p>
      <ul>
        <li>All data is encrypted in transit using TLS/SSL</li>
        <li>Data at rest is encrypted using AES-256</li>
        <li>Access to personal data is restricted to authorized personnel only</li>
        <li>We conduct regular security reviews</li>
      </ul>
      <p>However, no method of transmission over the Internet is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee its absolute security.</p>

      <div className="legal-divider" />

      <h2 id="retention">6. Data Retention</h2>
      <p>We retain your personal information for as long as your account is active or as needed to provide services. Click data is retained for up to 12 months for fraud analysis purposes. You may request deletion of your data at any time by contacting us.</p>

      <div className="legal-divider" />

      <h2 id="rights">7. Your Rights</h2>
      <p>Depending on your location, you may have the following rights regarding your personal data:</p>
      <ul>
        <li><strong>Access:</strong> Request a copy of your personal data</li>
        <li><strong>Correction:</strong> Request correction of inaccurate data</li>
        <li><strong>Deletion:</strong> Request deletion of your personal data</li>
        <li><strong>Portability:</strong> Request your data in a portable format</li>
        <li><strong>Objection:</strong> Object to certain processing activities</li>
        <li><strong>Withdrawal of Consent:</strong> Withdraw consent at any time</li>
      </ul>
      <p>To exercise these rights, please contact us at <a href="mailto:support@clicksaver.app">support@clicksaver.app</a>.</p>

      <div className="legal-divider" />

      <h2 id="children">8. Children's Privacy</h2>
      <p>Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it promptly.</p>

      <div className="legal-divider" />

      <h2 id="changes">9. Changes to This Policy</h2>
      <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by email or by posting a notice on our website. Your continued use of our services after changes take effect constitutes your acceptance of the updated policy.</p>

      <div className="legal-divider" />

      <h2 id="contact">10. Contact Us</h2>
      <p>If you have questions about this Privacy Policy, please contact us:</p>
      <ul>
        <li>Email: <a href="mailto:support@clicksaver.app">support@clicksaver.app</a></li>
        <li>Website: <a href="https://clicksaver.app">clicksaver.app</a></li>
      </ul>

    </LegalPage>
  );
}
